// Disabling so that I can organize the props by type while building the components
/* eslint react/jsx-sort-props: 0 */

import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { StyleShadowRoot } from "@redotech/react-util/style";
import {
  composeSeeDetailsUrl,
  ExtendedWarrantyPdpExperience,
  getCheapestOffering,
  Offering,
  widgetConfigToText,
} from "@redotech/redo-model/extended-warranty";
import { memo, useMemo, useState } from "react";
import STYLE_MANAGER from "../../style-manager";
import { useExpandedWarranties } from "../hooks/use-expanded-warranties";
import { useRenderPdps } from "../hooks/use-render-pdps";
import { useWindowVariantDetails } from "../hooks/use-window-variant-details";
import { ExtendedWarrantyModal } from "../modal";
import { EwSettingsContext } from "../provider/ew-settings-provider";
import { ExtendedWarrantyOptionsExperience } from "./experience";

export const ExtendedWarrantyOptions = memo(function ExtendedWarrantyOptions() {
  const { getWidgetConfig } = useRequiredContext(EwSettingsContext);

  const { variantDetails } = useWindowVariantDetails();

  const pdpPortals = useRenderPdps();

  const variantId = useMemo(() => {
    return variantDetails?.variantId;
  }, [variantDetails]);

  const trackedVariants = useMemo(() => {
    return variantDetails ? [variantDetails] : [];
  }, [variantDetails]);

  const {
    trackedVariantFlatOfferings,
    trackedVariantSelectedOffering,
    updateSelectedOffering,
  } = useExpandedWarranties({ trackedVariants });

  const offerings = useMemo(() => {
    if (!variantId) {
      return [];
    }
    return trackedVariantFlatOfferings[variantId] ?? ([] as Offering[]);
  }, [variantId, trackedVariantFlatOfferings]);

  const selectedOffering = useMemo(() => {
    if (!variantId) {
      return undefined;
    }
    return trackedVariantSelectedOffering[variantId];
  }, [variantId, trackedVariantSelectedOffering]);

  const cheapestOffering = useMemo(() => {
    if (!offerings) {
      return undefined;
    }
    return getCheapestOffering(offerings);
  }, [offerings]);

  // Widget config
  const widgetConfig = useMemo(() => {
    return getWidgetConfig();
  }, [getWidgetConfig]);

  const seeDetailsUrl = useMemo(() => {
    return (
      widgetConfig.modalSeeDetailsUrl ?? composeSeeDetailsUrl(location.host)
    );
  }, [widgetConfig]);

  const text = useMemo(() => {
    return widgetConfigToText(widgetConfig);
  }, [widgetConfig]);

  // View control
  const [showModal, setShowModal] = useState(false);

  const handleOpenModal = useHandler(() => {
    setShowModal(true);
  });

  const handleCloseModal = useHandler(() => {
    setShowModal(false);
  });

  const handleTileClick = useHandler((offering: Offering) => {
    if (!variantDetails) {
      return;
    }
    if (offering.title === selectedOffering?.title) {
      return;
    }
    updateSelectedOffering(variantDetails.variantId, offering);
  });

  if (offerings.length === 0 || selectedOffering === undefined) {
    return null;
  }

  if (pdpPortals.length > 0) {
    return pdpPortals.map(({ foundSelector, portal }, index) => (
      <StyleShadowRoot
        key={index}
        shadowRoot={portal.root}
        styleManager={STYLE_MANAGER}
      >
        <ExtendedWarrantyOptionsExperience
          // Offering settings
          offerings={offerings}
          cheapestOffering={cheapestOffering}
          selectedOffering={selectedOffering}
          // Widget settings
          customCss={widgetConfig.customPdpCss}
          experience={
            widgetConfig.pdpExperience ??
            ExtendedWarrantyPdpExperience.TILES_MODAL
          }
          text={text}
          // View control
          openModal={handleOpenModal}
          onTileClick={handleTileClick}
          foundSelector={foundSelector}
        />
        <ExtendedWarrantyModal
          // Offering settings
          offerings={offerings}
          selectedOffering={selectedOffering}
          // Widget settings
          text={text}
          customCss={widgetConfig.customModalCss}
          logo={widgetConfig.logo}
          sideImage={widgetConfig.sideImage}
          seeDetailsUrl={seeDetailsUrl}
          // View control
          open={showModal}
          onClose={handleCloseModal}
          onTileClick={handleTileClick}
          foundSelector={foundSelector}
        />
      </StyleShadowRoot>
    ));
  }

  return (
    <>
      <ExtendedWarrantyOptionsExperience
        // Offering settings
        offerings={offerings}
        cheapestOffering={cheapestOffering}
        selectedOffering={selectedOffering}
        // Widget settings
        customCss={widgetConfig.customPdpCss}
        experience={
          widgetConfig.pdpExperience ??
          ExtendedWarrantyPdpExperience.TILES_MODAL
        }
        text={text}
        // View control
        openModal={handleOpenModal}
        onTileClick={handleTileClick}
      />
      <ExtendedWarrantyModal
        // Offering settings
        offerings={offerings}
        selectedOffering={selectedOffering}
        // Widget settings
        text={text}
        customCss={widgetConfig.customModalCss}
        logo={widgetConfig.logo}
        sideImage={widgetConfig.sideImage}
        seeDetailsUrl={seeDetailsUrl}
        // View control
        open={showModal}
        onClose={handleCloseModal}
        onTileClick={handleTileClick}
      />
    </>
  );
});
